import styled, { css } from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { WoltInboundLoadItem as InboundLoadItemT } from 'common/api-types/warehouse';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { Thumbnail } from 'common/components/cookbook/Thumbnail';
import { Typography as T } from 'common/components/cookbook/Typography/Typography';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { ProgressCircle } from './ProgressCircle';
import { ProgressIndicator } from './ProgressIndicator';

const Root = 'div';

const MainContainer = styled(ContentContainer).attrs({ verticalPadding: 'sm' })`
  cursor: pointer;
  display: flex;
`;

const BottomBorderContainer = styled(ContentContainer)<{ isLastElement: boolean }>`
  padding-top: 0;
  padding-right: 0;
  padding-bottom: 0;
  ${({ isLastElement }) =>
    isLastElement &&
    css`
      padding-left: 0;
    `};
`;

const BottomBorder = styled.div`
  height: 1px;
  background: ${color.border};
`;

const LeftColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MiddleColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  margin-left: 16px;
`;

const RightColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MiddleColumnRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  & + & {
    margin-top: 8px;
  }
`;

const Gtin = styled(T.body)`
  margin-left: 8px;
`;

type Props = {
  inboundLoadPublicId?: string;
  inboundLoadItem: InboundLoadItemT;
  isLastElement: boolean;
  onClick: (itemId: string, inboundLoadId: string) => void;
};

export const InboundLoadItem = ({
  inboundLoadPublicId,
  inboundLoadItem,
  onClick,
  isLastElement,
}: Props) => {
  const formatMessage = useFormatMessage();

  const { name, gtin, image, orderedQuantity, receivedQuantity } = inboundLoadItem;

  const progress = (receivedQuantity.value / orderedQuantity.value) * 100;

  return (
    <Root data-test-id={`inbound-load-item-${inboundLoadPublicId}`}>
      <MainContainer
        onClick={(event) => {
          event.stopPropagation();
          onClick(inboundLoadItem.itemId, inboundLoadItem.inboundLoadId);
        }}
      >
        <LeftColumn>
          <Thumbnail imgSrc={image?.url} altText="Item name" />
        </LeftColumn>
        <MiddleColumn>
          <MiddleColumnRow>
            <T.bodyLabel>{name}</T.bodyLabel>
          </MiddleColumnRow>
          <MiddleColumnRow>
            <Icon source={icons.barcode} size="small" fill={color.text} />
            <Gtin>{gtin || formatMessage('common.not_available')}</Gtin>
          </MiddleColumnRow>
          <MiddleColumnRow>
            <ProgressIndicator
              receivedQuantity={receivedQuantity.value}
              orderedQuantity={orderedQuantity.value}
            />
          </MiddleColumnRow>
        </MiddleColumn>
        <RightColumn>
          <ProgressCircle progressPercentage={progress} />
        </RightColumn>
      </MainContainer>
      <BottomBorderContainer isLastElement={isLastElement}>
        <BottomBorder />
      </BottomBorderContainer>
    </Root>
  );
};
